import { matchPath, useRoutes } from "react-router-dom";
import { useEffect, useState } from "react";
// project import
import LoginRoutes from "./LoginRoutes";
import MainRoutes from "./MainRoutes";
import { HOME_PATH } from "@/config";
import Login from "@/pages/authentication/Login";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { isExpired } from "react-jwt";
import { LOGIN_AUTH, LOGOUT_AUTH } from '@/store/reducers/auth';
import { get_user_details } from '@/utils/api';

// ==============================|| ROUTING RENDER ||============================== //

export default function ThemeRoutes() {
  const userModel = useSelector((state) => state.auth.userModel);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Returning from the auth proxy (?login=success, no token in the URL): the session exchange lives in
  // the login page's AuthLogin, so render it right here — no hop through /login.
  const [ssoReturn, setSsoReturn] = useState(() => {
    const q = new URLSearchParams(window.location.search || '');
    return q.get('login') === 'success' && !q.get('access_token');
  });

  useEffect(() => {
    if (ssoReturn && location.pathname !== '/') setSsoReturn(false); // AuthLogin has navigated on: the return is over
    checkLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkLogin isn't memoized; adding it would re-run this on every render
  }, [userModel.isLogin, location.pathname]);

  function checkLogin() {
    if (ssoReturn) return; // AuthLogin owns this moment, whatever a stale persisted isLogin says
    // Supports both BrowserRouter (?a=1) and HashRouter (#/path?a=1) query styles.
    const hashQuery = window.location.hash?.includes('?')
      ? window.location.hash.split('?')[1]
      : '';
    const searchQuery = window.location.search?.startsWith('?')
      ? window.location.search.slice(1)
      : '';
    const params = new URLSearchParams(searchQuery || hashQuery);
    // A refused SSO login comes back to the app's front URL with ?error=; show it on the login page.
    if (window.location.pathname === '/' && params.get('error') && !params.get('access_token')) {
      navigate(`/login${window.location.search}`, { replace: true });
      return;
    }
    // Legacy token-in-URL SSO only. A bare ?login=success is the auth-proxy return, completed on
    // the login page (session exchange), so it must not be handled here.
    if (params.get('login') === 'success' && params.get('access_token')) {
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      if (accessToken) localStorage.setItem('Token', accessToken);
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
      localStorage.setItem('auth_mode', 'sso');

      // Validate token against backend before marking user as logged-in.
      get_user_details((details) => {
        const isProvisioned = Array.isArray(details) && details.length > 0;

        if (!isProvisioned) {
          localStorage.removeItem('Token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('auth_mode');
          dispatch(LOGOUT_AUTH());
          navigate('/login');
          return;
        }

        localStorage.setItem('user_details', JSON.stringify(details[0]));
        window.history.replaceState({}, document.title, window.location.pathname);
        dispatch(LOGIN_AUTH());
        navigate(HOME_PATH);
      });
      return;
    }
    const token = localStorage.getItem("Token");
    // Session-cookie auth has no client-side token; the backend enforces expiry
    // via the HttpOnly cookie (a 401 on any call triggers the global logout).
    // So in session mode we treat the "token" as always valid here and let the
    // cookie drive auth — otherwise the missing token reads as "expired" and
    // bounces the user back to /login right after a successful login.
    const isSessionMode = localStorage.getItem("auth_mode") === "session";
    const isMyTokenExpired = isSessionMode ? false : isExpired(token);

    if (!userModel.isLogin) {
      if (
        (location.pathname !== "/login" && location.pathname !== "/") &&
        isMyTokenExpired
      ) {
        LoginRoutes().children.some((item) => item.path === location.pathname)
          ? navigate(location.pathname)
          : navigate("/login");
        localStorage.setItem("lang", "en");
      }
    } else if (isMyTokenExpired && userModel.isLogin) {
      dispatch(LOGOUT_AUTH());
      navigate("/login?reason=expired");
    } else if (location.pathname === "/login" && !isMyTokenExpired) {
      // Only /login force-redirects; "/" redirects to the home page through the routes.
      dispatch(LOGIN_AUTH());
      navigate(HOME_PATH);
    } else if (
      !isMyTokenExpired &&
      userModel.isLogin &&
      !MainRoutes().children?.some((item) =>
        matchPath({ path: item.path, end: true }, location.pathname)
      )
    ) {
      dispatch(LOGOUT_AUTH());
      navigate("/login");
    } else {
      dispatch(LOGIN_AUTH());
    }
  }

  const routes = useRoutes([LoginRoutes(), userModel.isLogin && MainRoutes()]);
  return ssoReturn ? <Login /> : routes;
}
